import { useMemo } from 'react';

import {
  type CompositionV3BreakdownOptions,
  type CompositionV3Filters,
  type CustomRoleTaxonomySelection,
  EntKind,
  TaxonomyType,
} from '@revelio/data-access';

import { RICS_TAXONOMY_ID } from '../../../../../../shared/selection-lists/constants';
import { useTaxonomyTree } from '../../../../../../shared/selection-lists/hooks/useTaxonomyTree';
import { getRoleTaxonomyId } from '../../../../../../shared/selection-lists/utils/roleTaxonomy';
import type { GeographyMode } from '../../../../../../shared/user-preferences/types';
import { useFilterContext } from '../../../../filters/contexts/FilterProvider';
import {
  type Filter,
  FilterId,
  FilterType,
  type SingleValueFilter,
} from '../../../../filters/types/filters';
import {
  getCompositionV3BreakdownOptions,
  isLeafRoleFilter,
} from '../../../../filters/utils/composition-v3/getCompositionV3BreakdownOptions';
import { RICS_TAXONOMY_TREE_LEVELS } from '../../../../filters/utils/ricsTaxonomyTree';
import type { CustomSkillTreeNode } from '../../../../filters/utils/skillTaxonomyTree';
import {
  GEOGRAPHY_TAXONOMY_ID_BY_MODE,
  GEOGRAPHY_TAXONOMY_TREE_LEVELS,
  ROLE_TAXONOMY_TREE_LEVELS,
} from '../../../../filters/utils/taxonomy-tree-levels';

export type ExhaustiveBreakdownSecondaryDimension =
  | 'role'
  | 'geography'
  | 'rics'
  | 'skills';

export const TAXONOMY_BACKED_SHARE_OF_SEGMENT_DIMENSIONS: ReadonlySet<ExhaustiveBreakdownSecondaryDimension> =
  new Set(['role', 'geography', 'rics', 'skills']);

type ExhaustiveBreakdownIds = Partial<
  Pick<CompositionV3BreakdownOptions, 'role' | 'geography' | 'rics' | 'skill'>
>;

// EntKind → tree depth mappings. Trees are fetched with the *_TAXONOMY_TREE_LEVELS
// arrays, and `normalizeTaxonomyNodes` assigns ascending depths starting at 0.
// EntKinds beyond the tree's deepest fetched level (e.g. SkillLvl_6+) have no
// entry and fall back to "let the backend pick top N".
const ROLE_BREAKDOWN_LEVEL_TO_TREE_DEPTH: Partial<Record<EntKind, number>> = {
  [EntKind.RoleLvl_1]: 0,
  [EntKind.RoleLvl_3]: 1,
  [EntKind.RoleLvl_5]: 2,
  [EntKind.RoleLvl_7]: 3,
};

const GEOGRAPHY_BREAKDOWN_LEVEL_TO_TREE_DEPTH: Partial<
  Record<EntKind, number>
> = {
  [EntKind.Region]: 0,
  [EntKind.Country]: 1,
  [EntKind.Msa]: 2,
  [EntKind.State]: 2,
  [EntKind.City]: 3,
};

const RICS_BREAKDOWN_LEVEL_TO_TREE_DEPTH: Partial<Record<EntKind, number>> = {
  [EntKind.RicsK10]: 0,
  [EntKind.RicsK50]: 1,
  [EntKind.RicsK400]: 2,
};

const SKILL_BREAKDOWN_LEVEL_TO_TREE_DEPTH: Partial<Record<EntKind, number>> = {
  [EntKind.SkillLvl_1]: 0,
  [EntKind.SkillLvl_2]: 1,
  [EntKind.SkillLvl_3]: 2,
  [EntKind.SkillLvl_4]: 3,
  [EntKind.SkillLvl_5]: 4,
};

type TreeLikeNode = {
  id: string;
  depth?: number;
  children?: readonly TreeLikeNode[];
};

const extractTrailingNumericId = (id: string): number | null => {
  const tail = id.includes('_') ? (id.split('_').pop() ?? '') : id;
  const n = Number(tail);
  return Number.isFinite(n) ? n : null;
};

const collectIdsAtDepth = (
  nodes: ReadonlyArray<TreeLikeNode> | null | undefined,
  targetDepth: number
): number[] => {
  if (!nodes?.length) return [];
  const out = new Set<number>();
  const stack: TreeLikeNode[] = [...nodes];
  while (stack.length > 0) {
    const node = stack.pop();
    if (!node) continue;
    if (node.depth === targetDepth) {
      const n = extractTrailingNumericId(node.id);
      if (n !== null) out.add(n);
    }
    if (node.children?.length) {
      stack.push(...node.children);
    }
  }
  return Array.from(out);
};

const isSingleValueFilter = <T>(
  f: Filter | undefined,
  id: FilterId
): f is SingleValueFilter<T> =>
  Boolean(f && f.type === FilterType.VALUE && f.id === id);

export const buildExhaustiveBreakdownDimensionsKey = (
  secondaryDimensions: ReadonlyArray<string | undefined>
): string => {
  const taxonomyBacked = new Set<ExhaustiveBreakdownSecondaryDimension>();
  for (const d of secondaryDimensions) {
    if (
      d !== undefined &&
      TAXONOMY_BACKED_SHARE_OF_SEGMENT_DIMENSIONS.has(
        d as ExhaustiveBreakdownSecondaryDimension
      )
    ) {
      taxonomyBacked.add(d as ExhaustiveBreakdownSecondaryDimension);
    }
  }
  return Array.from(taxonomyBacked).sort().join(',');
};

const hasDimension = (
  key: string,
  dimension: ExhaustiveBreakdownSecondaryDimension
): boolean => {
  if (key === '') return false;
  return key.split(',').includes(dimension);
};

export type UseExhaustiveBreakdownIdsV3Result = {
  exhaustiveBreakdownIds: ExhaustiveBreakdownIds | undefined;
  isExhaustiveBreakdownIdsReady: boolean;
};

export const useExhaustiveBreakdownIdsV3 = ({
  secondaryDimensionsKey,
  compositionFilters,
  geographyMode,
  customSkillTree,
  customSkillDeepestLevel,
  isCustomSkillTaxonomyPending = false,
}: {
  secondaryDimensionsKey: string;
  compositionFilters: CompositionV3Filters;
  geographyMode: GeographyMode;
  customSkillTree: CustomSkillTreeNode[] | null;
  customSkillDeepestLevel:
    | 'skill_0'
    | 'skill_1'
    | 'skill_2'
    | 'skill_3'
    | 'skill_4'
    | null;
  isCustomSkillTaxonomyPending?: boolean;
}): UseExhaustiveBreakdownIdsV3Result => {
  const { activeFilterMap } = useFilterContext();

  const breakdownLevels = useMemo(
    () =>
      getCompositionV3BreakdownOptions({
        compositionFilters,
        customSkillTree,
        customSkillDeepestLevel,
        geographyMode,
      }),
    [
      compositionFilters,
      customSkillTree,
      customSkillDeepestLevel,
      geographyMode,
    ]
  );

  // Derive the active role taxonomy id (mirrors FilterProvider so the cached
  // query result is shared across consumers).
  const roleTaxonomySelection = (() => {
    const roleTaxonomyFilter = activeFilterMap.get(FilterId.ROLE_TAXONOMY);
    if (
      !isSingleValueFilter<CustomRoleTaxonomySelection>(
        roleTaxonomyFilter,
        FilterId.ROLE_TAXONOMY
      )
    ) {
      return undefined;
    }
    return roleTaxonomyFilter.value;
  })();
  const roleReferenceTaxonomyId = useMemo(
    () => getRoleTaxonomyId(roleTaxonomySelection),
    [roleTaxonomySelection]
  );

  const wantsRole = hasDimension(secondaryDimensionsKey, 'role');
  const wantsGeography = hasDimension(secondaryDimensionsKey, 'geography');
  const wantsRics = hasDimension(secondaryDimensionsKey, 'rics');
  const wantsSkill = hasDimension(secondaryDimensionsKey, 'skills');

  const roleTreePaused = !wantsRole || roleReferenceTaxonomyId === undefined;
  const { treeData: roleTreeData, fetching: isRoleTreeFetching } =
    useTaxonomyTree({
      taxonomyType: TaxonomyType.Role,
      taxonomyId: roleReferenceTaxonomyId,
      levels: ROLE_TAXONOMY_TREE_LEVELS,
      pause: roleTreePaused,
    });

  const { treeData: geographyTreeData, fetching: isGeographyTreeFetching } =
    useTaxonomyTree({
      taxonomyType: TaxonomyType.Geography,
      taxonomyId: GEOGRAPHY_TAXONOMY_ID_BY_MODE[geographyMode],
      levels: GEOGRAPHY_TAXONOMY_TREE_LEVELS,
      pause: !wantsGeography,
    });

  const { treeData: ricsTreeData, fetching: isRicsTreeFetching } =
    useTaxonomyTree({
      taxonomyType: TaxonomyType.Rics,
      taxonomyId: RICS_TAXONOMY_ID,
      levels: RICS_TAXONOMY_TREE_LEVELS,
      pause: !wantsRics,
    });

  const hasSkillTreeData =
    Array.isArray(customSkillTree) && customSkillTree.length > 0;
  const isSkillReady =
    !wantsSkill || hasSkillTreeData || !isCustomSkillTaxonomyPending;

  const isExhaustiveBreakdownIdsReady =
    secondaryDimensionsKey === '' ||
    ((!wantsRole || roleTreePaused || !isRoleTreeFetching) &&
      (!wantsGeography || !isGeographyTreeFetching) &&
      (!wantsRics || !isRicsTreeFetching) &&
      isSkillReady);

  const exhaustiveBreakdownIds = useMemo(():
    | ExhaustiveBreakdownIds
    | undefined => {
    if (secondaryDimensionsKey === '') return undefined;

    const result: ExhaustiveBreakdownIds = {};

    if (wantsRole) {
      if (!isLeafRoleFilter(compositionFilters)) {
        const depth =
          ROLE_BREAKDOWN_LEVEL_TO_TREE_DEPTH[
            breakdownLevels.role_breakdown_level
          ];
        if (depth !== undefined) {
          const ids = collectIdsAtDepth(roleTreeData, depth);
          if (ids.length > 0) result.role = ids;
        }
      }
    }

    if (wantsGeography) {
      const depth =
        GEOGRAPHY_BREAKDOWN_LEVEL_TO_TREE_DEPTH[
          breakdownLevels.geo_breakdown_level
        ];
      if (depth !== undefined) {
        const ids = collectIdsAtDepth(geographyTreeData, depth);
        if (ids.length > 0) result.geography = ids;
      }
    }

    if (wantsRics) {
      const depth =
        RICS_BREAKDOWN_LEVEL_TO_TREE_DEPTH[
          breakdownLevels.rics_breakdown_level
        ];
      if (depth !== undefined) {
        const ids = collectIdsAtDepth(ricsTreeData, depth);
        if (ids.length > 0) result.rics = ids;
      }
    }

    if (wantsSkill) {
      const depth =
        SKILL_BREAKDOWN_LEVEL_TO_TREE_DEPTH[
          breakdownLevels.skill_breakdown_level
        ];
      if (depth !== undefined) {
        const ids = collectIdsAtDepth(customSkillTree, depth);
        if (ids.length > 0) result.skill = ids;
      }
    }

    return Object.keys(result).length > 0 ? result : undefined;
  }, [
    secondaryDimensionsKey,
    wantsRole,
    wantsGeography,
    wantsRics,
    wantsSkill,
    breakdownLevels,
    compositionFilters,
    roleTreeData,
    geographyTreeData,
    ricsTreeData,
    customSkillTree,
  ]);

  return { exhaustiveBreakdownIds, isExhaustiveBreakdownIdsReady };
};
